/**
 * FR4 — «Generation settings» popover (mock 03 right): text model, thinking
 * effort, output budget. Saved locally per project (useAdCreativeGenSettings)
 * and passed explicitly into angles/copy/storyboard calls. TEXT models only —
 * the Veo VIDEO model keeps its dedicated select (explicit label).
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { AvailableModelSelector } from '@modules/ai-settings';
import { AD_CREATIVE_GEN_SETTINGS } from '../../selectors';
import {
  UI_EFFORT_BUDGET_FLOORS,
  type AdCreativeGenSettings,
} from '../../hooks/useAdCreativeGenSettings';

const BUDGET_PRESETS = [
  { value: 8_000, label: 'Standard · 8k' },
  { value: 16_000, label: 'Large · 16k' },
  { value: 32_000, label: 'Max · 32k' },
] as const;

export function GenerationSettingsPopover({
  projectId,
  settings,
  onUpdate,
  onReset,
}: {
  projectId: string;
  settings: AdCreativeGenSettings;
  onUpdate: (patch: Partial<AdCreativeGenSettings>) => void;
  onReset: () => void;
}): React.ReactElement {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const customized = Boolean(settings.textModel || settings.reasoningEffort || settings.maxOutputTokens != null);
  const effortFloor = settings.reasoningEffort ? UI_EFFORT_BUDGET_FLOORS[settings.reasoningEffort] : 0;

  return (
    <>
      <Tooltip title="Generation settings (text model, thinking, budget)">
        <IconButton
          size="small"
          data-testid={AD_CREATIVE_GEN_SETTINGS.trigger}
          data-customized={customized ? 'true' : 'false'}
          onClick={(event) => setAnchor(event.currentTarget)}
          color={customized ? 'primary' : 'default'}
        >
          <TuneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, p: 2 } } }}
      >
        <Stack spacing={1.5} data-testid={AD_CREATIVE_GEN_SETTINGS.popover}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Generation settings</Typography>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Text model (angles, copy, storyboard) — video keeps its own Veo select
            </Typography>
            <AvailableModelSelector
              projectId={projectId}
              value={settings.textModel ?? ''}
              providers={['google']}
              onChange={(model: string) => onUpdate({ textModel: model || undefined })}
            />
          </Box>

          <Box data-testid={AD_CREATIVE_GEN_SETTINGS.effort}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Thinking effort
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={settings.reasoningEffort ?? 'default'}
              onChange={(_, next) => {
                if (next) onUpdate({ reasoningEffort: next === 'default' ? undefined : next });
              }}
            >
              <ToggleButton value="default" sx={{ px: 1.25, fontSize: 11 }}>Default</ToggleButton>
              <ToggleButton value="low" sx={{ px: 1.25, fontSize: 11 }}>Low</ToggleButton>
              <ToggleButton value="medium" sx={{ px: 1.25, fontSize: 11 }}>Medium</ToggleButton>
              <ToggleButton value="high" sx={{ px: 1.25, fontSize: 11 }}>High</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box data-testid={AD_CREATIVE_GEN_SETTINGS.budget}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Output budget
            </Typography>
            <Stack direction="row" spacing={0.75}>
              <Chip
                size="small"
                label="Default"
                variant={settings.maxOutputTokens == null ? 'filled' : 'outlined'}
                color={settings.maxOutputTokens == null ? 'primary' : 'default'}
                onClick={() => onUpdate({ maxOutputTokens: undefined })}
                disabled={Boolean(settings.reasoningEffort)}
              />
              {BUDGET_PRESETS.map((preset) => {
                // The UI cannot express a combo the server rejects (F4): budget
                // presets below the effort floor are disabled.
                const belowFloor = preset.value < effortFloor;
                return (
                  <Chip
                    key={preset.value}
                    size="small"
                    data-testid={AD_CREATIVE_GEN_SETTINGS.budgetPreset(preset.value)}
                    label={preset.label}
                    variant={settings.maxOutputTokens === preset.value ? 'filled' : 'outlined'}
                    color={settings.maxOutputTokens === preset.value ? 'primary' : 'default'}
                    disabled={belowFloor}
                    onClick={() => onUpdate({ maxOutputTokens: preset.value })}
                  />
                );
              })}
            </Stack>
          </Box>

          <Typography variant="caption" color="text.secondary" data-testid={AD_CREATIVE_GEN_SETTINGS.warning}>
            ⚠️ gemini-3 thinking shares the output budget — raising effort raises the minimum budget
            automatically so JSON never truncates.
          </Typography>

          <Stack direction="row" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Saved locally for this project · applies to angles, copy & storyboard
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button size="small" data-testid={AD_CREATIVE_GEN_SETTINGS.reset} onClick={onReset}>
              Reset
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
